import React, { useEffect, useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ScrollView,
} from 'react-native';
import { applyTransparency } from '../../constants/colors';
import { PopupV2 } from './PopupV2';
import ButtonGeneral from './ButtonGeneral';
import {
  GAME_OPERATION_TYPE,
  GAME_TYPE_ARRANGEMENT,
  GAME_TYPES_CONFIG,
  GAME_TYPES_TYPE,
} from '../../constants/constants';

type RadioProps = {
  label: string;
  selected: boolean;
  onPress: () => void;
  textStyle?: TextStyle;
};

const RadioButton = ({ label, selected, onPress, textStyle }: RadioProps) => {
  return (
    <TouchableOpacity style={styles.radioContainer} onPress={onPress}>
      <View style={[styles.radioOuter, selected && { borderColor: '#E87030' }]}>
        {selected && <View style={styles.radioInner} />}
      </View>
      <Text style={[styles.label, textStyle]}>{label}</Text>
    </TouchableOpacity>
  );
};

interface IPopupProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
  onSavePress: (data: GAME_TYPES_TYPE) => void;
  game?: GAME_TYPES_TYPE;
}

const SelectGamesPopUp = ({
  visible,
  setVisible,
  onSavePress,
  game = GAME_TYPES_TYPE.ADD_1_12,
}: IPopupProps) => {
  const { width } = useWindowDimensions();
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  const isMobile = isMobileWeb || Platform.OS !== 'web';
  const flexDirection = isMobile ? 'column' : 'row';

  const [selectedGame, setSelectedGame] = useState<GAME_TYPES_TYPE>(game);

  useEffect(() => {
    setSelectedGame(game || GAME_TYPES_TYPE.ADD_1_12);
  }, [game, visible]);

  const renderOperationSection = (operation: GAME_OPERATION_TYPE, title: string) => (
    <View style={styles.operationsContainer}>
      <Text style={styles.operationTitle}>{title}</Text>
      {GAME_TYPE_ARRANGEMENT.filter(type => GAME_TYPES_CONFIG[type].operation === operation).map(
        type => (
          <RadioButton
            key={type}
            label={GAME_TYPES_CONFIG[type].name}
            selected={selectedGame === type}
            onPress={() => setSelectedGame(type)}
          />
        ),
      )}
    </View>
  );

  const GameListContent = (
    <>
      <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        {renderOperationSection(GAME_OPERATION_TYPE.ADDITION, 'Addition')}
        {renderOperationSection(GAME_OPERATION_TYPE.SUBTRACTION, 'Subtraction')}
        {renderOperationSection(GAME_OPERATION_TYPE.DIVISION, 'Division')}
      </View>
      <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        {renderOperationSection(GAME_OPERATION_TYPE.MULTIPLICATION, 'Multiplication')}
      </View>
    </>
  );

  return (
    <PopupV2
      height={isMobile ? 570 : 'auto'}
      title="Select Game Type"
      visible={visible}
      setVisible={setVisible}
      closeOnBackdropPress={true}
      containerStyle={{
        backgroundColor: '#FCEABB',
        borderColor: '#FFCF53',
        width: isMobile ? 348 : 700,
        maxWidth: 1000,
        padding: 0,
      }}
      headerTextStyle={{ fontSize: isMobile ? 18 : 24 }}
      hideCloseButton={true}
    >
      <View style={styles.container}>
        {flexDirection === 'column' ? (
          <ScrollView
            style={{ maxHeight: 500, marginTop: 15 }}
            contentContainerStyle={[
              styles.gamesContainer,
              { flexDirection, alignItems: 'flex-start' },
            ]}
          >
            {GameListContent}
          </ScrollView>
        ) : (
          <View style={[styles.gamesContainer, { flexDirection, alignItems: 'flex-start' }]}>
            {GameListContent}
          </View>
        )}

        <View style={[styles.buttonContainer, { height: isMobile ? 80 : 102 }]}>
          <View style={{ marginRight: isMobile ? 8 : 12 }}>
            <ButtonGeneral
              onPress={() => {
                setVisible(false);
              }}
              title={'Cancel'}
              height={isMobile ? 48 : 58}
              width={isMobile ? 151 : 201}
              fontSize={isMobile ? 14 : 16}
              fontStyle={{ color: '#000000' }}
              colors={['#FEDD88', '#FEDD88']}
              nameContainerStyle={{
                borderColor: '#ffffff',
                borderWidth: 2,
                marginRight: isMobile ? 10 : 0,
              }}
            />
          </View>
          <ButtonGeneral
            onPress={() => onSavePress(selectedGame)}
            title={`Save${isMobile ? '' : ' changes'}`}
            height={isMobile ? 48 : 58}
            width={isMobile ? 151 : 201}
            fontSize={isMobile ? 14 : 16}
            fontStyle={{ color: '#000000' }}
            colors={['#FFCF53', '#FF9900']}
          />
        </View>
      </View>
    </PopupV2>
  );
};

export { SelectGamesPopUp };

const styles = StyleSheet.create({
  title: {
    fontFamily: 'Secular One',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#121212',
    fontSize: 18,
  },
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 20,
  },
  label: {
    fontFamily: 'Secular One',
    fontSize: 19,
    fontWeight: '400',
    color: '#121212',
  },
  buttonContainer: {
    justifyContent: 'center',
    backgroundColor: '#FEDD88',
    alignItems: 'center',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    flexDirection: 'row',
    width: '100%',
    marginTop: 0,
  },
  gamesContainer: {
    flexDirection: 'row',
    width: '100%',
    padding: 20,
    gap: 10,
    justifyContent: 'space-around',
  },
  operationsContainer: {
    marginTop: 24,
    position: 'relative',
  },
  radioContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#1D1B2059',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#E87030',
  },
  operationTitle: {
    fontFamily: 'Secular One',
    fontSize: 14,
    fontWeight: '600',
    color: '#121212',
    marginBottom: 12,
  },
});
